import { BaseConfigurator, ARROW_SVG } from './base-configurator.js';
import { CartAddEvent } from '@theme/events';

class AppConfigurator extends BaseConfigurator {
  requiredRefs = [
    'drawer', 'backdrop', 'closeBtn', 'stepNav', 'stepContent',
    'summary', 'totalPrice', 'addToCart', 'addToCartText', 'addToCartSpinner',
    'qtyWrap', 'qtyMinus', 'qtyPlus', 'qtyInput', 'validationMessage',
  ];

  /** @type {import('./configurator-storefront-types').StorefrontConfiguratorPayload | null} */
  #config = null;
  /** @type {Promise | null} */
  #configPromise = null;
  #variantId = null;
  #basePriceCents = 0;
  // For grouped steps: tracks the selected sub-option ID separately from the parent value ID
  #subSelections = {};

  parseConfig() {
    this._currencyCode = this.dataset.currency || 'EUR';
    this.#variantId = this.dataset.variantId;
    this.#basePriceCents = parseInt(this.dataset.basePriceCents, 10) || 0;

    const productId = this.dataset.productId;
    const appProxy = this.dataset.appProxy || '/apps/configurator';
    if (!productId) return;

    this.#configPromise = fetch(`${appProxy}?product_id=${productId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        this.#config = data;
        if (data.product?.defaultVariantId) {
          // GID → numeric: "gid://shopify/ProductVariant/12345" → "12345"
          const gid = data.product.defaultVariantId;
          this.#variantId = gid.includes('/') ? gid.split('/').pop() : gid;
        }
        if (data.product?.basePriceCents != null) this.#basePriceCents = data.product.basePriceCents;
        return data;
      })
      .catch((err) => {
        console.error('[AppConfigurator] Config fetch failed:', err);
        throw err;
      });
  }

  setupFooterEvents() {
    const { addToCart, qtyMinus, qtyPlus, qtyInput } = this.refs;
    addToCart.addEventListener('click', (e) => this.#handleFooterClick(e));
    qtyMinus.addEventListener('click', () => this.#adjustQty(-1));
    qtyPlus.addEventListener('click', () => this.#adjustQty(1));
    qtyInput.addEventListener('change', () => this.#clampQty());
  }

  bindContentEvents(content) {
    content.addEventListener('click', (e) => {
      const btn = e.target?.closest?.('[data-step-key][data-value]');
      if (!btn || btn.getAttribute('role') !== 'radio') return;
      e.preventDefault();
      this.#handleOptionClick(btn);
    });
  }

  async openDrawer() {
    if (this._isOpen) return;

    // Open drawer immediately — close button is already wired by _setupDrawer in connectedCallback
    this._isOpen = true;
    this._savedScrollY = window.scrollY;
    this._previousActiveEl = document.activeElement;

    this._backdropEl.hidden = false;
    this._drawerEl.hidden = false;
    document.body.style.overflow = 'hidden';

    this._keyHandler = (e) => {
      if (e.key === 'Escape') this.closeDrawer();
      else if (e.key === 'Tab') this._trapFocus(e);
    };
    document.addEventListener('keydown', this._keyHandler);

    requestAnimationFrame(() => {
      this._backdropEl?.classList.add('configurator__backdrop--visible');
      this._drawerEl?.classList.add('configurator__drawer--open');
    });

    if (!this._initialized) {
      const loadingEl = this.refs.loadingState;
      const errorEl = this.refs.errorState;
      if (loadingEl) loadingEl.hidden = false;

      try {
        await this.#configPromise;
        this.#renderSteps();
        if (loadingEl) loadingEl.hidden = true;
        this.onInit();
        this._bindEvents();
        this._showStep(0);
        this._updateTabState();
        this.updateSummary();
        this.updatePrice();
        this.updateFooterButton();
      } catch {
        if (loadingEl) loadingEl.hidden = true;
        if (errorEl) errorEl.hidden = false;
        return;
      }

      this._initialized = true;
    }

    const firstFocusable = this._drawerEl?.querySelector('button:not([disabled]), input');
    if (firstFocusable instanceof HTMLElement) firstFocusable.focus();
  }

  onInit() {
    this._selections = {};
    this.#subSelections = {};
  }

  updateSummary() {
    const summary = this.refs.summary;
    if (!summary || !this.#config) return;
    summary.innerHTML = '';
    const list = document.createElement('dl');
    list.className = 'configurator__summary-list';

    for (const group of this.#config.groups) {
      const valueId = this._selections[group.id];
      if (!valueId) continue;
      const value = group.values.find((v) => v.id === valueId);
      if (!value) continue;

      const dt = document.createElement('dt');
      dt.className = 'configurator__summary-label';
      dt.textContent = group.label;

      const dd = document.createElement('dd');
      dd.className = 'configurator__summary-value';
      const subOptionId = this.#subSelections[group.id];
      if (subOptionId) {
        const sub = value.subOptions?.find((s) => s.id === subOptionId);
        dd.textContent = sub ? `${value.label} — ${sub.label}` : value.label;
      } else {
        dd.textContent = value.label;
      }

      list.append(dt, dd);
    }
    summary.append(list);
  }

  updatePrice() {
    if (this.refs.totalPrice) {
      this.refs.totalPrice.textContent = this.#formatPrice(this.#calculateTotalCents());
    }
  }

  updateFooterButton() {
    const btn = this.refs.addToCart;
    const label = this.refs.addToCartText;
    const qtyWrap = this.refs.qtyWrap;
    if (!btn || !label) return;

    const isLastStep = this._currentStepIndex >= this._steps.length - 1;
    if (qtyWrap) qtyWrap.hidden = !isLastStep;

    if (isLastStep) {
      label.textContent = 'In den Warenkorb';
      btn.disabled = !this.#isValid();
      if (this.#isValid() && this.refs.validationMessage) {
        this.refs.validationMessage.hidden = true;
      }
    } else {
      const next = this._steps[this._currentStepIndex + 1];
      label.innerHTML = next ? `Weiter zu ${next.label} ${ARROW_SVG}` : `Weiter ${ARROW_SVG}`;
      btn.disabled = false;
    }
  }

  #renderSteps() {
    const config = this.#config;
    if (!config?.groups?.length) return;

    const nav = this._navEl;
    const content = this._contentEl;
    if (!nav || !content) return;

    this._steps = [];

    for (let i = 0; i < config.groups.length; i++) {
      const group = config.groups[i];
      this._steps.push({ key: group.id, label: group.label });

      // Tab button
      const li = document.createElement('li');
      li.className = 'configurator__tab-item';
      const tabBtn = document.createElement('button');
      tabBtn.type = 'button';
      tabBtn.className = 'configurator__tab-button';
      tabBtn.dataset.stepIndex = String(i);
      tabBtn.setAttribute('aria-current', i === 0 ? 'step' : 'false');
      tabBtn.innerHTML = `<span class="configurator__tab-number">${i + 1}</span><span>${group.label}</span>`;
      li.appendChild(tabBtn);
      nav.appendChild(li);

      // Step panel
      const panel = document.createElement('div');
      panel.className = 'configurator__step';
      panel.dataset.stepIndex = String(i);
      panel.dataset.stepKey = group.id;
      if (i > 0) panel.hidden = true;
      panel.setAttribute('aria-hidden', i > 0 ? 'true' : 'false');

      const heading = document.createElement('h3');
      const hasSubOptions = group.values.some((v) => v.subOptions?.length > 0);
      heading.className = hasSubOptions
        ? 'configurator__step-heading configurator__step-heading--sr-only'
        : 'configurator__step-heading';
      heading.textContent = group.label;
      panel.appendChild(heading);

      if (hasSubOptions) {
        panel.appendChild(this.#buildGroupedOptions(group));
      } else {
        panel.appendChild(this.#buildSimpleOptions(group));
      }

      content.appendChild(panel);
    }
  }

  #buildGroupedOptions(group) {
    const wrap = document.createElement('div');
    wrap.className = 'configurator__options configurator__options--grouped';

    for (const value of group.values) {
      if (!value.isActive || !value.subOptions?.length) continue;

      const section = document.createElement('section');
      section.className = 'configurator__option-group';
      section.setAttribute('aria-labelledby', `cfg-group-${value.id}`);

      const headingWrap = document.createElement('div');
      headingWrap.className = 'configurator__group-heading-scheme color-scheme-1';
      const heading = document.createElement('h4');
      heading.id = `cfg-group-${value.id}`;
      heading.className = 'configurator__option-group-heading';
      heading.textContent = value.label;
      headingWrap.appendChild(heading);
      section.appendChild(headingWrap);

      const optionsDiv = document.createElement('div');
      optionsDiv.className = 'configurator__options configurator__options--buttons configurator__options--cards';
      optionsDiv.setAttribute('role', 'radiogroup');
      optionsDiv.setAttribute('aria-label', value.label);

      for (const sub of value.subOptions) {
        if (!sub.isActive) continue;
        optionsDiv.appendChild(this.#buildOptionCard({
          stepKey: group.id,
          valueId: sub.id,
          label: sub.label,
          surchargeCents: (value.surchargeCents ?? 0) + (sub.surchargeCents ?? 0),
          color: sub.color,
          image: sub.image,
          parentValueId: value.id,
        }));
      }

      section.appendChild(optionsDiv);
      wrap.appendChild(section);
    }
    return wrap;
  }

  #buildSimpleOptions(group) {
    const div = document.createElement('div');
    div.className = 'configurator__options configurator__options--buttons configurator__options--cards';
    div.setAttribute('role', 'radiogroup');
    div.setAttribute('aria-label', group.label);

    for (const value of group.values) {
      if (!value.isActive) continue;
      div.appendChild(this.#buildOptionCard({
        stepKey: group.id,
        valueId: value.id,
        label: value.label,
        surchargeCents: value.surchargeCents,
        color: value.color,
        image: value.image,
      }));
    }
    return div;
  }

  #buildOptionCard({ stepKey, valueId, label, surchargeCents, color, image, parentValueId }) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'configurator__option-button configurator__option-card';
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-checked', 'false');
    btn.setAttribute('aria-label', label);
    btn.dataset.stepKey = stepKey;
    btn.dataset.value = valueId;
    btn.dataset.label = label;
    if (parentValueId) btn.dataset.parentValueId = parentValueId;

    // Image wrap
    const imageWrap = document.createElement('div');
    imageWrap.className = 'configurator__option-card-image-wrap';
    imageWrap.setAttribute('aria-hidden', 'true');

    const imageSrc = image?.url;
    if (imageSrc) {
      const img = document.createElement('img');
      img.src = imageSrc;
      img.alt = '';
      img.className = 'configurator__option-card-image';
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      imageWrap.appendChild(img);
    } else if (color?.hex) {
      const swatch = document.createElement('span');
      swatch.className = 'configurator__option-card-swatch';
      swatch.style.backgroundColor = color.hex;
      imageWrap.appendChild(swatch);
    }
    btn.appendChild(imageWrap);

    // Zoom trigger
    if (imageSrc) {
      const zoom = document.createElement('span');
      zoom.className = 'configurator__zoom-trigger';
      zoom.setAttribute('role', 'button');
      zoom.tabIndex = 0;
      zoom.setAttribute('aria-label', 'Zoom image');
      zoom.setAttribute('aria-haspopup', 'dialog');
      zoom.dataset.zoomSrc = imageSrc;
      zoom.dataset.zoomAlt = label;
      zoom.innerHTML =
        '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
        '<circle cx="7.5" cy="7.5" r="5.75" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M12 12l4.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg>';
      btn.appendChild(zoom);
    }

    // Info row
    const info = document.createElement('div');
    info.className = 'configurator__option-card-info';

    const labelSpan = document.createElement('span');
    labelSpan.className = 'configurator__option-card-label';
    labelSpan.textContent = label;
    info.appendChild(labelSpan);

    const priceSpan = document.createElement('span');
    priceSpan.className = 'configurator__option-card-price';
    if (surchargeCents > 0) {
      priceSpan.textContent = `+${this.#formatPrice(surchargeCents)}`;
    }
    info.appendChild(priceSpan);

    btn.appendChild(info);
    return btn;
  }

  #handleFooterClick(e) {
    if (this._currentStepIndex >= this._steps.length - 1) {
      this.#handleAddToCart(e);
    } else {
      e.preventDefault();
      this._showStep(this._currentStepIndex + 1);
    }
  }

  #handleOptionClick(button) {
    const stepKey = button.getAttribute('data-step-key');
    const valueId = button.getAttribute('data-value');
    const parentValueId = button.getAttribute('data-parent-value-id');
    if (!stepKey || !valueId) return;

    if (parentValueId) {
      // Sub-option selected: store parent as the selection (surcharge source), sub for display
      this._selections[stepKey] = parentValueId;
      this.#subSelections[stepKey] = valueId;
    } else {
      this._selections[stepKey] = valueId;
      delete this.#subSelections[stepKey];
    }

    const stepPanel = button.closest('.configurator__step');
    if (stepPanel) {
      stepPanel.querySelectorAll('[role="radio"]').forEach((r) => {
        const isChecked = r === button;
        r.setAttribute('aria-checked', isChecked ? 'true' : 'false');
        r.classList.toggle('configurator__option-card--active', isChecked);
        r.classList.toggle('configurator__option-button--active', isChecked);
      });
    }

    this._afterSelectionChange();
  }

  async #handleAddToCart(e) {
    e.preventDefault();
    if (!this.#isValid()) {
      const el = this.refs.validationMessage;
      if (el) {
        el.textContent = 'Bitte füllen Sie alle Pflichtfelder aus.';
        el.hidden = false;
      }
      return;
    }

    const addButton = this.refs.addToCart;
    const buttonText = this.refs.addToCartText;
    const spinner = this.refs.addToCartSpinner;
    addButton.disabled = true;
    buttonText.textContent = 'Wird hinzugefügt…';
    if (spinner) spinner.hidden = false;

    try {
      const properties = this.#buildCartProperties();
      const quantity = Math.max(
        parseInt(this.refs.qtyInput?.min, 10) || 1,
        parseInt(this.refs.qtyInput?.value, 10) || 1
      );

      const sectionIds = [];
      for (const el of document.querySelectorAll('cart-items-component')) {
        if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10_000);

      const payload = { id: this.#variantId, quantity, properties };
      if (sectionIds.length > 0) payload.sections = sectionIds.join(',');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

      const addData = await response.json();

      const cartPromise = Promise.race([
        fetch('/cart.js').then((r) => r.json()).catch(() => null),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
      ]);

      buttonText.textContent = 'Hinzugefügt ✓';
      const [cart] = await Promise.all([cartPromise, new Promise((resolve) => setTimeout(resolve, 1200))]);

      this.closeDrawer();
      buttonText.textContent = 'In den Warenkorb';
      addButton.disabled = false;
      if (spinner) spinner.hidden = true;

      await new Promise((resolve) => setTimeout(resolve, 450));

      this.dispatchEvent(
        new CartAddEvent(cart || {}, String(this.#variantId), {
          productId: this.dataset.productId,
          ...(cart && { itemCount: cart.item_count }),
          ...(addData.sections && { sections: addData.sections }),
        })
      );
    } catch (err) {
      console.error('[AppConfigurator] Add to cart error:', err);
      buttonText.textContent =
        err.name === 'AbortError' ? 'Zeitüberschreitung – erneut versuchen' : 'Fehler – erneut versuchen';
      addButton.disabled = false;
      if (spinner) spinner.hidden = true;
    }
  }

  #calculateTotalCents() {
    let total = this.#basePriceCents;
    if (!this.#config) return total;
    for (const group of this.#config.groups) {
      const valueId = this._selections[group.id];
      if (!valueId) continue;
      const value = group.values.find((v) => v.id === valueId);
      total += value?.surchargeCents ?? 0;
      const subOptionId = this.#subSelections[group.id];
      if (subOptionId) {
        const sub = value?.subOptions?.find((s) => s.id === subOptionId);
        total += sub?.surchargeCents ?? 0;
      }
    }
    return total;
  }

  #isValid() {
    for (const step of this._steps) {
      if (!this._selections[step.key]) return false;
    }
    return true;
  }

  #buildCartProperties() {
    const properties = {};
    if (!this.#config) return properties;

    const cfgSelections = {};
    for (const group of this.#config.groups) {
      const valueId = this._selections[group.id];
      if (!valueId) continue;
      const value = group.values.find((v) => v.id === valueId);
      if (!value) continue;

      const subOptionId = this.#subSelections[group.id];
      if (subOptionId) {
        const sub = value.subOptions?.find((s) => s.id === subOptionId);
        properties[group.label] = sub ? `${value.label} — ${sub.label}` : value.label;
        cfgSelections[group.id] = { valueId, subOptionId };
      } else {
        properties[group.label] = value.label;
        cfgSelections[group.id] = valueId;
      }
    }

    // Hidden from customer (underscore prefix) — read by Cart Transform Function for server-side pricing
    properties['_cfg_selections'] = JSON.stringify(cfgSelections);

    return properties;
  }

  #adjustQty(delta) {
    const input = this.refs.qtyInput;
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 99;
    const current = parseInt(input.value, 10);
    const val = Math.min(max, Math.max(min, (isNaN(current) ? min : current) + delta));
    input.value = isNaN(val) ? min : val;
  }

  #clampQty() {
    const input = this.refs.qtyInput;
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 99;
    let val = parseInt(input.value, 10);
    if (isNaN(val) || val < min) val = min;
    if (val > max) val = max;
    input.value = val;
  }

  #formatPrice(cents) {
    return this._priceFormatter.format(cents / 100);
  }
}

customElements.define('custom-app-configurator', AppConfigurator);
